import { useCallback, useMemo, useState } from 'react'
import { collectErrorMessages } from '../lib/validators'
import { useStepSubmit } from '../context/StepSubmitContext'

// Shared step-validation visibility. Every step uses handleSubmit(onValid,
// onInvalid); a zod failure used to only pulse() + focusFirstError() with no
// message, so Continue looked dead. This hook standardizes:
//   - a live, readable list of what's wrong (errorMessages)
//   - a submitAttempted flag so the banner only appears after a Continue try
//   - an onInvalid handler that logs, reveals the banner, pulses, and focuses
//
// Usage:
//   const { submitAttempted, errorMessages, onInvalid } =
//     useStepErrorSummary(STEP_ID, errors)
//   ...register submit as handleSubmit(onValid, onInvalid)...
//   <ErrorBanner show={submitAttempted} messages={errorMessages} />
export function useStepErrorSummary(stepId, errors) {
  const { pulse, focusFirstError } = useStepSubmit()
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const errorMessages = useMemo(() => collectErrorMessages(errors), [errors])

  const onInvalid = useCallback(
    (formErrors) => {
      // formErrors is the fresh errors object react-hook-form passes in.
      console.warn(`[${stepId}] validation blocked`, formErrors || errors)
      setSubmitAttempted(true)
      pulse()
      setTimeout(() => focusFirstError(), 0)
    },
    // errors intentionally omitted: formErrors arg is always the fresh copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepId, pulse, focusFirstError],
  )

  return { submitAttempted, setSubmitAttempted, errorMessages, onInvalid }
}
